import type { Guide, GuideId } from "../types";

/**
 * Tag affiliato Amazon. Cambialo qui per propagare ovunque.
 * Iscrizione: https://affiliazione.amazon.it
 */
export const AMAZON_AFFILIATE_TAG = "powertower-21";

/**
 * Costruisce un URL di ricerca Amazon.it con il tag affiliato.
 * Utile finche' non hai gli ASIN esatti dei singoli libri.
 */
const amazonSearch = (query: string): string =>
  `https://www.amazon.it/s?k=${encodeURIComponent(
    query
  )}&tag=${AMAZON_AFFILIATE_TAG}`;

/**
 * Registro delle guide.
 *
 * Quando aggiungerai nuove guide (50 Top Pizza, Identita' Golose, Dissapore...),
 * aggiungi qui sopra il GuideId in src/types.ts e poi una nuova entry sotto.
 *
 * `amazonUrl` e' valorizzato solo se la guida esiste come libro acquistabile
 * su Amazon. Per guide solo digitali (es. 50 Top Pizza, Michelin) si lascia
 * vuoto: il bottone "compra la guida" non comparira' per quel POI.
 */
export const GUIDES: Record<GuideId, Guide> = {
  michelin: {
    id: "michelin",
    name: "Guida Michelin",
    publisher: "Michelin",
    homepage: "https://guide.michelin.com/it/it",
    edition: "2026",
    amazonUrl: amazonSearch("guida michelin italia"),
    description:
      "La guida gastronomica piu' prestigiosa al mondo. Le stelle Michelin (1, 2 o 3) riconoscono cucine di eccellenza secondo criteri internazionali; il Bib Gourmand premia ristoranti di qualita' a prezzo onesto.",
    tiers: [
      { id: "3-stars", label: "3 Stelle", rank: 100 },
      { id: "2-stars", label: "2 Stelle", rank: 90 },
      { id: "1-star", label: "1 Stella", rank: 80 },
      { id: "bib-gourmand", label: "Bib Gourmand", rank: 60 },
    ],
  },
  "gambero-rosso-ristoranti": {
    id: "gambero-rosso-ristoranti",
    name: "Ristoranti d'Italia",
    publisher: "Gambero Rosso",
    homepage: "https://www.gamberorosso.it/guide/ristoranti-ditalia/",
    edition: "2026",
    amazonUrl: amazonSearch("ristoranti d'italia gambero rosso"),
    description:
      "La guida storica del Gambero Rosso ai migliori ristoranti italiani: 2.600 indirizzi recensiti, con un punteggio da 0 a 100. Le 'Tre Forchette' (90-100/100) sono l'olimpo della ristorazione italiana; le 'Due Forchette' migliori (85-89/100) sono ristoranti di altissimo livello. La guida assegna anche premi paralleli per categorie specifiche: 'Tre Gamberi' alle migliori trattorie, 'Tre Bottiglie' ai wine bar, 'Tre Mappamondi' ai ristoranti internazionali, 'Tre Tavole' ai migliori bistrot.",
    tiers: [
      { id: "3-forchette", label: "3 Forchette", rank: 95 },
      { id: "2-forchette", label: "2 Forchette", rank: 75 },
      { id: "3-gamberi", label: "3 Gamberi", rank: 65 },
      { id: "3-bottiglie", label: "3 Bottiglie", rank: 65 },
      { id: "3-mappamondi", label: "3 Mappamondi", rank: 65 },
      { id: "3-tavole", label: "3 Tavole", rank: 65 },
    ],
  },
  "gambero-rosso-pizzerie": {
    id: "gambero-rosso-pizzerie",
    name: "Pizzerie d'Italia",
    publisher: "Gambero Rosso",
    homepage: "https://www.gamberorosso.it/guide/pizzerie-ditalia/",
    edition: "2026",
    amazonUrl: amazonSearch("pizzerie d'italia gambero rosso"),
    description:
      "Guida annuale del Gambero Rosso alle migliori pizzerie d'Italia. Le pizzerie sono valutate con un sistema di 'spicchi' (massimo 3) per la classica e 'rotelle' (massimo 3) per la pizza al taglio/tonda romana.",
    tiers: [
      { id: "3-spicchi", label: "3 Spicchi", rank: 80 },
      { id: "3-rotelle", label: "3 Rotelle", rank: 75 },
    ],
  },
  "gambero-rosso-gelaterie": {
    id: "gambero-rosso-gelaterie",
    name: "Gelaterie d'Italia",
    publisher: "Gambero Rosso",
    homepage: "https://www.gamberorosso.it/guide/gelaterie-ditalia/",
    edition: "2025",
    amazonUrl: amazonSearch("gelaterie d'italia gambero rosso"),
    description:
      "Guida annuale del Gambero Rosso alle migliori gelaterie. Le gelaterie sono valutate con un sistema di 'coni' (massimo 3).",
    tiers: [{ id: "3-coni", label: "3 Coni", rank: 70 }],
  },
  "gambero-rosso-sushi": {
    id: "gambero-rosso-sushi",
    name: "Sushi & altre cucine asiatiche",
    publisher: "Gambero Rosso",
    homepage: "https://www.gamberorosso.it/guide/",
    edition: "2026",
    amazonUrl: amazonSearch("sushi gambero rosso"),
    description:
      "Selezione del Gambero Rosso dei migliori ristoranti di sushi e cucina asiatica in Italia, valutati con un sistema di 'bacchette' (massimo 3).",
    tiers: [{ id: "3-bacchette", label: "3 Bacchette", rank: 70 }],
  },
  "gambero-rosso-caffe": {
    id: "gambero-rosso-caffe",
    name: "Bar d'Italia",
    publisher: "Gambero Rosso",
    homepage: "https://www.gamberorosso.it/guide/bar-ditalia/",
    edition: "2026",
    amazonUrl: amazonSearch("bar d'italia gambero rosso"),
    description:
      "Guida annuale del Gambero Rosso ai migliori bar d'Italia. I locali ricevono Tre Tazzine (per la pasticceria/colazione) e/o Tre Chicchi (per la qualita' del caffe'). Le Due Stelle e la Stella sono onorificenze ulteriori assegnate a bar che si distinguono particolarmente.",
    tiers: [
      { id: "3-chicchi-2-stelle", label: "3 Chicchi + 2 Stelle", rank: 85 },
      { id: "3-chicchi-1-stella", label: "3 Chicchi + 1 Stella", rank: 80 },
      { id: "3-chicchi", label: "3 Chicchi", rank: 70 },
    ],
  },
  "gambero-rosso-pasticcerie": {
    id: "gambero-rosso-pasticcerie",
    name: "Pasticceri & Pasticcerie d'Italia",
    publisher: "Gambero Rosso",
    homepage: "https://www.gamberorosso.it/guide/pasticceri-pasticcerie-ditalia/",
    edition: "2026",
    amazonUrl: amazonSearch("pasticcerie d'italia gambero rosso"),
    description:
      "Guida annuale del Gambero Rosso alle migliori pasticcerie. I locali sono valutati con un sistema di 'torte' (massimo 3).",
    tiers: [{ id: "3-torte", label: "3 Torte", rank: 70 }],
  },
  "gambero-rosso-street-food": {
    id: "gambero-rosso-street-food",
    name: "Street Food d'Italia",
    publisher: "Gambero Rosso",
    homepage: "https://www.gamberorosso.it/guide/street-food/",
    edition: "2026",
    amazonUrl: amazonSearch("street food gambero rosso"),
    description:
      "Selezione del Gambero Rosso dei migliori indirizzi di street food in Italia.",
  },
  "slow-food-osterie": {
    id: "slow-food-osterie",
    name: "Osterie d'Italia",
    publisher: "Slow Food Editore",
    homepage: "https://www.slowfood.it/guida-osterie/",
    edition: "2026",
    amazonUrl: amazonSearch("osterie d'italia slow food"),
    description:
      "La guida storica di Slow Food alle osterie italiane: cucine territoriali, prezzo onesto, accoglienza. Il riconoscimento massimo e' la 'Chiocciola'.",
    tiers: [
      { id: "chiocciola", label: "Chiocciola", rank: 70 },
      { id: "bottiglia", label: "Bottiglia", rank: 50 },
      { id: "menzione", label: "Menzione", rank: 40 },
    ],
  },
  "espresso-ristoranti": {
    id: "espresso-ristoranti",
    name: "Ristoranti d'Italia",
    publisher: "L'Espresso",
    homepage: "https://guideespresso.it/restaurants/list?type=1&hats=5",
    edition: "2026",
    amazonUrl: amazonSearch("guida espresso ristoranti d'italia"),
    description:
      "La guida ristoranti de L'Espresso usa un sistema di 'Cappelli' (da 1 a 5) e punteggi in 'ventesimi' (15-20). I '5 Cappelli' (21 nel 2026), i '4 Cappelli' (45) e i '3 Cappelli' (83) rappresentano i 149 migliori ristoranti d'Italia secondo la guida.",
    tiers: [
      { id: "5-cappelli", label: "5 Cappelli", rank: 95 },
      { id: "4-cappelli", label: "4 Cappelli", rank: 80 },
      { id: "3-cappelli", label: "3 Cappelli", rank: 65 },
    ],
  },
};
